import { ethers } from "ethers";
import config from "../config";
import { config as wagmiConfig } from "../../wagmi.config";
import { useSlippageState } from "../contexts/slippageContext";
import BigNumber from "bignumber.js";
import { writeContract, waitForTransactionReceipt } from "@wagmi/core";
import useLazyReadContractAsync from "./useLazyReadContractAsync";
import AMMRouter from "../../src/assets/abi/swap/AMMRouter.json";
import DMMRouter from "../../src/assets/abi/swap/DMMRouter.json";
import ERC20ABI from "../../src/assets/abi/farm/ERC20.json";
import EvryPairABI from "../assets/abi/swap/EvryPair.json";
import DMMPoolABI from "../assets/abi/swap/DMMPool.json";
import { useEffect, useState } from "react";

const noOpAny = (value: any) => {};

// Keep track of already logged missing addresses to reduce spam
const loggedMissingAddresses = new Set<string>();

const useLiquidity = () => {
  const [isReady, setIsReady] = useState(false);
  const { lazyReadContract } = useLazyReadContractAsync();

  const { slippage } = useSlippageState();

  useEffect(() => {
    setIsReady(true);
  }, []);

  const getListReserveInPool = async (lpNames: string[]) => {
    try {
      return await Promise.all(
        lpNames.map((lpName) => getReserveInPool(lpName))
      );
    } catch (e) {
      console.log("getListReserveInPool", e);
    }
    return [];
  };

  const getReserveInPool = async (lpName: string) => {
    // Check LP configurations first, then token config as fallback
    let tokenConfig = config.lp[lpName];
    if (!tokenConfig) {
      tokenConfig = config.dmmLP[lpName];
    }
    // Remove the fallback to config.token since Token type is incompatible with LiquidityPool
    
    if (tokenConfig?.address) {
      try {
        // Determine which ABI to use based on LP type
        let abi = EvryPairABI; // Default to AMM ABI
        if (tokenConfig.lpType === "DMM") {
          abi = DMMPoolABI;
        }
        
        const result = await lazyReadContract(
          abi,
          tokenConfig.address,
          "getReserves",
          []
        );
        return result;
      } catch (e) {
        console.error("getReserveInPool error for", lpName, e);
      }
    } else {
      // Only log missing address once per LP token to reduce console spam
      if (!loggedMissingAddresses.has(lpName)) {
        loggedMissingAddresses.add(lpName);
      }
    }
    return "0";
  };

  const getTotalPoolShare = async (lpName: string, address: string) => {
    // Look for LP token address in lp or dmmLP config, not token config
    let lpConfig = config.lp[lpName];
    if (!lpConfig) {
      lpConfig = config.dmmLP[lpName];
    }
    
    if (lpConfig?.address) {
      const totalSupply = await getLpTotalSupply(lpName);
      const lpBalance = await getBalanceOfLP(lpName, address);
      return {
        totalSupply,
        lpBalance,
      };
    }
    return {
      totalSupply: "0",
      lpBalance: "0",
    };
  };

  const getTokenConfigByAddress = (address: string) => {
    let token = null;
    const configTokenList = Object.keys(config.token).map((k) => {
      return config.token[k];
    });
    token = configTokenList.find(
      (element) =>
        element?.address &&
        element.address.toUpperCase() === address.toUpperCase()
    );

    return token;
  };

  const getToken0AndToken1InLp = async (lpName: string) => {
    let token0address: any = "0";
    let token1address: any = "0";
    try {
      // Use LP config instead of token config
      const lpConfig = config.lp[lpName] || config.dmmLP[lpName];
      
      if (lpConfig?.address) {
        // Use EvryPairABI for token0/token1 calls
        token0address = await lazyReadContract(
          EvryPairABI,
          lpConfig.address,
          "token0",
          []
        );
        token1address = await lazyReadContract(
          EvryPairABI,
          lpConfig.address,
          "token1",
          []
        );
      }
    } catch (e) {
      console.log("Error in getToken0AndToken1InLp:", e);
    }

    const token0 = getTokenConfigByAddress(token0address.toString());
    const token1 = getTokenConfigByAddress(token1address.toString());
    return { token0, token1 };
  };

  const getBalanceOfLP = async (lpName: string, account: string) => {
    // Look for LP token address in lp or dmmLP config, not token config
    let lpConfig = config.lp[lpName];
    if (!lpConfig) {
      lpConfig = config.dmmLP[lpName];
    }
    
    if (lpConfig?.address) {
      try {
        return await lazyReadContract(
          ERC20ABI,
          lpConfig.address,
          "balanceOf",
          [account]
        );
      } catch (e) {
        console.error("getBalanceOfLP", lpName, account, e);
      }
    }
    return "0";
  };

  const getLpTotalSupply = async (lpName: string) => {
    // Look for LP token address in lp or dmmLP config, not token config
    let lpConfig = config.lp[lpName];
    if (!lpConfig) {
      lpConfig = config.dmmLP[lpName];
    }
    
    if (lpConfig?.address) {
      return await lazyReadContract(
        ERC20ABI,
        lpConfig.address,
        "totalSupply",
        []
      );
    }
    return "0";
  };

  const addLiquidityETHOnAMM = async (
    ethAmount: string,
    tokenAddress: string,
    tokenAmount: string,
    minTokenAmount: string,
    amountEthMin: string,
    wallet: string,
    deadline: string,
    { onError = noOpAny, onReceipt = noOpAny, onTransactionHash = noOpAny }
  ) => {
    try {
      const hash = await writeContract(wagmiConfig, {
        abi: AMMRouter,
        value: BigInt(ethAmount),
        address: config.ammRouterContractAddress as `0x${string}`,
        functionName: "addLiquidityETH",
        args: [
          tokenAddress as `0x${string}`,
          BigInt(tokenAmount),
          BigInt(minTokenAmount),
          BigInt(amountEthMin),
          wallet as `0x${string}`,
          BigInt(deadline || 1),
        ],
      });

      // Trigger transactionHash callback
      onTransactionHash(hash);

      // Wait for the transaction to be mined
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });

      // Trigger receipt callback
      onReceipt({ transactionHash: receipt.transactionHash, status: receipt.status === "success" });
      console.log("addLiquidityETH success", receipt);

      return { transactionHash: receipt.transactionHash, status: receipt.status === "success" };
    } catch (error) {
      console.log("addLiquidityETH error", error);
      onError(error);
      throw error;
    }
    // ethereum.contracts.ammRouter.methods
    //   .addLiquidityETH(
    //     tokenAddress,
    //     tokenAmount,
    //     minTokenAmount,
    //     amountEthMin,
    //     wallet,
    //     deadline
    //   )
    //   .send({
    //     from: wallet,
    //     value: ethAmount,
    //   })
    //   .on("transactionHash", onTransactionHash)
    //   .on("receipt", onReceipt)
    //   .catch(onError);
  };

  const addLiquidityOnAMM = async (
    tokenA: string,
    tokenB: string,
    amountA: string,
    amountB: string,
    amountAMin: string,
    amountBMin: string,
    wallet: string,
    deadline: string,
    { onError = noOpAny, onReceipt = noOpAny, onTransactionHash = noOpAny }
  ) => {
    try {
      const hash = await writeContract(wagmiConfig, {
        abi: AMMRouter,
        address: config.ammRouterContractAddress as `0x${string}`,
        functionName: "addLiquidity",
        args: [
          tokenA as `0x${string}`,
          tokenB as `0x${string}`,
          BigInt(amountA),
          BigInt(amountB),
          BigInt(amountAMin),
          BigInt(amountBMin),
          wallet as `0x${string}`,
          BigInt(deadline),
        ],
      });

      // Trigger transactionHash callback
      onTransactionHash(hash);

      // Wait for the transaction to be mined
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });

      // Trigger receipt callback
      onReceipt({ transactionHash: receipt.transactionHash, status: receipt.status === "success" });
      console.log("addLiquidity success", receipt);

      return { transactionHash: receipt.transactionHash, status: receipt.status === "success" };
      // ethereum.contracts.ammRouter.methods
      //   .addLiquidity(
      //     tokenA,
      //     tokenB,
      //     amountA,
      //     amountB,
      //     amountAMin,
      //     amountBMin,
      //     wallet,
      //     deadline
      //   )
      //   .send({
      //     from: wallet,
      //   })
      //   .on("transactionHash", onTransactionHash)
      //   .on("receipt", onReceipt)
      //   .catch(onError);
    } catch (error) {
      console.log("addLiquidityOnAMM error", error);
      onError(error);
      throw error;
    }
  };

  const addLiquidityOnDMM = async (
    tokenA: string,
    tokenB: string,
    poolAddress: string,
    amountTokenA: string,
    amountTokenB: string,
    amountAMin: string,
    amountBMin: string,
    wallet: string,
    trxDeadline: string,
    { onError = noOpAny, onReceipt = noOpAny, onTransactionHash = noOpAny }
  ) => {
    try {
      const reserveRatio = [0, ethers.MaxUint256];
      const hash = await writeContract(wagmiConfig, {
        abi: DMMRouter,
        address: config.dmmRouterContractAddress as `0x${string}`,
        functionName: "addLiquidity",
        args: [
          tokenA as `0x${string}`,
          tokenB as `0x${string}`,
          poolAddress as `0x${string}`,
          BigInt(amountTokenA),
          BigInt(amountTokenB),
          BigInt(amountAMin),
          BigInt(amountBMin),
          reserveRatio,
          wallet as `0x${string}`,
          BigInt(trxDeadline),
        ],
      });

      // Trigger transactionHash callback
      onTransactionHash(hash);

      // Wait for the transaction to be mined
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });

      // Trigger receipt callback
      onReceipt({ transactionHash: receipt.transactionHash, status: receipt.status === "success" });
      console.log("addLiquidityOnDMM success", receipt);

      return { transactionHash: receipt.transactionHash, status: receipt.status === "success" };
    } catch (error) {
      console.log("addLiquidityOnDMM error", error);
      onError(error);
      throw error;
    }
    // ethereum.contracts.dmmRouter.methods
    //   .addLiquidity(
    //     tokenA,
    //     tokenB,
    //     poolAddress,
    //     amountTokenA,
    //     amountTokenB,
    //     amountAMin,
    //     amountBMin,
    //     reserveRatio,
    //     wallet,
    //     trxDeadline
    //   )
    //   .send({
    //     from: wallet,
    //   })
    //   .on("transactionHash", onTransactionHash)
    //   .on("receipt", onReceipt)
    //   .catch(onError);
  };

  const slippageCalculation = (estimateAmountOut: any) => {
    const amountOutMin = BigNumber(estimateAmountOut).times(slippage).div(100);
    return BigNumber(estimateAmountOut)
      .minus(amountOutMin)
      .toFixed(17)
      .toString();
  };

  const removeLiquidityETHOnAMM = async (
    tokenAddress: string,
    liquidity: string,
    amountTokenMin: string,
    amountNativeMin: string,
    deadline: string,
    wallet: string,
    { onError = noOpAny, onReceipt = noOpAny, onTransactionHash = noOpAny }
  ) => {
    try {
      const hash = await writeContract(wagmiConfig, {
        abi: AMMRouter,
        address: config.ammRouterContractAddress as `0x${string}`,
        functionName: "removeLiquidityETH",
        args: [
          tokenAddress as `0x${string}`,
          BigInt(liquidity),
          BigInt(amountTokenMin),
          BigInt(amountNativeMin),
          wallet as `0x${string}`,
          BigInt(deadline),
        ],
      });

      // Trigger transactionHash callback
      onTransactionHash(hash);

      // Wait for the transaction to be mined
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });

      // Trigger receipt callback
      onReceipt({ transactionHash: receipt.transactionHash, status: receipt.status === "success" });
      console.log("removeLiquidityETH success", receipt);

      return { transactionHash: receipt.transactionHash, status: receipt.status === "success" };
    } catch (error) {
      console.log("removeLiquidityETHOnAMM error", error);
      onError(error);
      throw error;
    }

    // ethereum.contracts.ammRouter.methods
    //   .removeLiquidityETH(
    //     tokenAddress,
    //     liquidity,
    //     amountTokenMin,
    //     amountNativeMin,
    //     wallet,
    //     deadline
    //   )
    //   .send({ from: wallet })
    //   .on("transactionHash", onTransactionHash)
    //   .on("receipt", onReceipt)
    //   .catch(onError);
  };

  const removeLiquidityOnAMM = async (
    tokenA: string,
    tokenB: string,
    liquidity: string,
    amountAMin: string,
    amountBMin: string,
    wallet: string,
    trxDeadline: string,
    { onError = noOpAny, onReceipt = noOpAny, onTransactionHash = noOpAny }
  ) => {
    try {
      const hash = await writeContract(wagmiConfig, {
        abi: AMMRouter,
        address: config.ammRouterContractAddress as `0x${string}`,
        functionName: "removeLiquidity",
        args: [
          tokenA as `0x${string}`,
          tokenB as `0x${string}`,
          BigInt(liquidity),
          BigInt(amountAMin),
          BigInt(amountBMin),
          wallet as `0x${string}`,
          BigInt(trxDeadline),
        ],
      });

      // Trigger transactionHash callback
      onTransactionHash(hash);

      // Wait for the transaction to be mined
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });

      // Trigger receipt callback
      onReceipt({ transactionHash: receipt.transactionHash, status: receipt.status === "success" });
      console.log("removeLiquidity success", receipt);

      return { transactionHash: receipt.transactionHash, status: receipt.status === "success" };
    } catch (error) {
      console.log("removeLiquidityOnAMM error", error);
      onError(error);
      throw error;
    }
    // ethereum.contracts.ammRouter.methods
    //   .removeLiquidity(
    //     tokenA,
    //     tokenB,
    //     liquidity,
    //     amountAMin,
    //     amountBMin,
    //     wallet,
    //     trxDeadline
    //   )
    //   .send({ from: wallet })
    //   .on("transactionHash", onTransactionHash)
    //   .on("receipt", onReceipt)
    //   .catch(onError);
  };

  const removeLiquidityOnDMM = async (
    tokenA: string,
    tokenB: string,
    poolAddress: string,
    liquidity: string,
    amountAMin: string,
    amountBMin: string,
    wallet: string,
    trxDeadline: string,
    { onError = noOpAny, onReceipt = noOpAny, onTransactionHash = noOpAny }
  ) => {
    try {
      const hash = await writeContract(wagmiConfig, {
        abi: DMMRouter,
        address: config.dmmRouterContractAddress as `0x${string}`,
        functionName: "removeLiquidity",
        args: [
          tokenA as `0x${string}`,
          tokenB as `0x${string}`,
          poolAddress as `0x${string}`,
          BigInt(liquidity),
          BigInt(amountAMin),
          BigInt(amountBMin),
          wallet as `0x${string}`,
          BigInt(trxDeadline),
        ],
      });

      // Trigger transactionHash callback
      onTransactionHash(hash);

      // Wait for the transaction to be mined
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });

      // Trigger receipt callback
      onReceipt({ transactionHash: receipt.transactionHash, status: receipt.status === "success" });
      console.log("removeLiquidityOnDMM success", receipt);

      return { transactionHash: receipt.transactionHash, status: receipt.status === "success" };
    } catch (error) {
      console.log("removeLiquidityOnDMM error", error);
      onError(error);
      throw error;
    }
    // ethereum.contracts.dmmRouter.methods
    //   .removeLiquidity(
    //     tokenA,
    //     tokenB,
    //     poolAddress,
    //     liquidity,
    //     amountAMin,
    //     amountBMin,
    //     wallet,
    //     trxDeadline
    //   )
    //   .send({ from: wallet })
    //   .on("transactionHash", onTransactionHash)
    //   .on("receipt", onReceipt)
    //   .catch(onError);
  };

  return {
    getListReserveInPool,
    getReserveInPool,
    getTokenConfigByAddress,
    getToken0AndToken1InLp,
    getBalanceOfLP,
    getLpTotalSupply,
    getTotalPoolShare,
    addLiquidityETHOnAMM,
    addLiquidityOnAMM,
    addLiquidityOnDMM,
    slippageCalculation,
    removeLiquidityETHOnAMM,
    removeLiquidityOnAMM,
    removeLiquidityOnDMM,
    isReady,
  };
};

export default useLiquidity;
